import { theme, fonts } from "../theme";

// ─── CARD (no animation — instant render) ───────────────────
export function Card({ children, style, glow, ...props }) {
  return (
    <div style={{ background: theme.white, borderRadius: 16, boxShadow: glow || theme.shadow2, overflow: "hidden", ...style }} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, sub, right }) {
  return (
    <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${theme.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, letterSpacing: "-0.2px" }}>{title}</div>
        {sub && <div style={{ fontSize: 11.5, color: theme.text3, marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function StatCard({ label, value, sub, delta, color, icon, style: es }) {
  const isPos = delta && !String(delta).startsWith("-");
  return (
    <Card style={{ padding: "20px 24px", ...es }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1.2px" }}>{label}</span>
        {icon && <span style={{ fontSize: 16, color: theme.text4, lineHeight: 1 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, fontFamily: fonts.mono, color: color || theme.text, lineHeight: 1, letterSpacing: "-1.5px" }}>{value}</div>
      {(delta || sub) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          {delta && <span style={{ fontSize: 12, fontWeight: 700, fontFamily: fonts.mono, color: isPos ? theme.greenText : theme.redText, padding: "2px 8px", borderRadius: 6, background: isPos ? theme.greenLight : theme.redLight }}>{delta}</span>}
          {sub && <span style={{ fontSize: 11, color: theme.text3 }}>{sub}</span>}
        </div>
      )}
    </Card>
  );
}

export function Pill({ children, bg, color }) {
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 6, fontFamily: fonts.mono, background: bg, color, display: "inline-block" }}>{children}</span>;
}

export function Badge({ children, variant = "default" }) {
  const v = { default: { bg: theme.tint, color: theme.text2 }, green: { bg: theme.greenLight, color: theme.greenText }, red: { bg: theme.redLight, color: theme.redText }, amber: { bg: theme.amberLight, color: theme.amberText }, blue: { bg: theme.blueLight, color: theme.blueText }, purple: { bg: theme.purpleLight, color: theme.purpleText } }[variant] || { bg: theme.tint, color: theme.text2 };
  return <Pill bg={v.bg} color={v.color}>{children}</Pill>;
}

export function StatusBadge({ status }) {
  // Status-Modell (Sprint 1.4): test/paper/live + paused/stopped/error
  const m = {
    test:    { bg: theme.tint || "#f1f5f9", color: theme.text3, dot: theme.text4 || "#94a3b8" },
    paper:   { bg: theme.blueLight,  color: theme.blueText,  dot: theme.blue },
    live:    { bg: theme.greenLight, color: theme.greenText, dot: theme.green },
    paused:  { bg: theme.amberLight, color: theme.amberText, dot: theme.amber },
    stopped: { bg: theme.redLight,   color: theme.redText,   dot: theme.red },
    error:   { bg: theme.redLight,   color: theme.redText,   dot: theme.red },
  }[status] || { bg: theme.redLight, color: theme.redText, dot: theme.red };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 11px 4px 8px", borderRadius: 20, background: m.bg, fontSize: 11, fontWeight: 700, color: m.color }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, animation: status === "live" ? "liveDot 2.5s infinite" : "none" }} />
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown"}
    </span>
  );
}

export function Button({ children, primary, danger, warning, style: es, ...props }) {
  const bg = primary ? theme.blue : danger ? theme.redLight : warning ? theme.amberLight : theme.white;
  const clr = primary ? "#fff" : danger ? theme.redText : warning ? theme.amberText : theme.text2;
  const bdr = primary ? "none" : danger ? "1px solid rgba(220,38,38,0.15)" : warning ? "1px solid rgba(217,119,6,0.15)" : `1px solid ${theme.border}`;
  return <button style={{ fontSize: 11, fontWeight: 700, padding: "7px 16px", borderRadius: 8, border: bdr, background: bg, color: clr, cursor: "pointer", fontFamily: fonts.body, transition: "all 0.15s", ...es }} {...props}>{children}</button>;
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 3, padding: 4, borderRadius: 10, background: theme.tint, flexWrap: "wrap" }}>
      {tabs.map((t) => (
        <button key={t} onClick={() => onChange(t)} style={{ padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: active === t ? 700 : 500, background: active === t ? theme.white : "transparent", color: active === t ? theme.text : theme.text3, boxShadow: active === t ? theme.shadow1 : "none", transition: "all 0.15s", fontFamily: fonts.body }}>{t}</button>
      ))}
    </div>
  );
}

export function SectionTitle({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, color: theme.text3, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14 }}>{children}</div>;
}

export function Metric({ label, value, color, small }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 600, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: small ? 13 : 15, fontWeight: 700, fontFamily: fonts.mono, color: color || theme.text, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}><div style={{ width: 32, height: 32, border: `3px solid ${theme.divider}`, borderTopColor: theme.blue, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /></div>;
}

export function ErrorState({ error, onRetry }) {
  return <Card style={{ padding: 32, textAlign: "center" }}><div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div><div style={{ fontSize: 14, fontWeight: 600, color: theme.redText, marginBottom: 4 }}>Connection Error</div><div style={{ fontSize: 12, color: theme.text3, marginBottom: 16 }}>{error}</div>{onRetry && <Button primary onClick={onRetry}>Retry</Button>}</Card>;
}

export function AccentStripe({ positive }) {
  return <div style={{ height: 3, background: positive ? `linear-gradient(90deg, ${theme.greenBright}, rgba(22,163,74,0.2))` : `linear-gradient(90deg, ${theme.red}, rgba(220,38,38,0.15))` }} />;
}

export function RegimePill({ regime }) {
  const m = { Trending: { bg: "rgba(22,163,74,0.07)", color: theme.greenText }, Ranging: { bg: "rgba(217,119,6,0.07)", color: theme.amberText }, Choppy: { bg: "rgba(220,38,38,0.05)", color: theme.redText } }[regime] || { bg: theme.tint, color: theme.text3 };
  return <Pill bg={m.bg} color={m.color}>{regime || "Unknown"}</Pill>;
}

export function formatPnl(val) {
  if (val == null) return "$0.00";
  const n = Number(val);
  // sign-by-color design: positive → "+" prefix (caller renders green), negative →
  // no prefix (caller renders red). Use `> 0` (not `>= 0`): `>=` let 0 AND -0 fall
  // into the "+" branch → a zero (e.g. a $0 cost cell from -(0)) rendered as a
  // contradictory "+$0.00"/red. `> 0` excludes both 0 and -0 → "$0.00", no sign.
  return `${n > 0 ? "+" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
export function formatPercent(val) { return val == null ? "0.0%" : `${Number(val).toFixed(1)}%`; }
export function pnlColor(val) { return val == null || val === 0 ? theme.text3 : val > 0 ? theme.greenText : theme.redText; }
